import type React from "react";
import { useTranslation } from "react-i18next";

import type { DataTableColumn } from "./types";
import "./data-table.css";
import { TablePlaceHolder } from "../../LoadingAnimations/LoadingAnimations";

type DataTableProps<T> = {
  columns: Array<DataTableColumn<T>>;
  data: Array<T>;
  isLoading: boolean;
  getRowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
};

/**
 * Helper function to get the value of a cell based on
 * if it has a custom cell renderer or just the raw value
 */
function getCellValue<T>(row: T, column: DataTableColumn<T>): React.ReactNode {
  if (column.cell) {
    return column.cell(row);
  }

  if (column.accessor) {
    const value = column.accessor(row);
    return value === null || value === undefined || value === "" ? "-" : value;
  }

  if (column.field) {
    const value = row[column.field];
    return value === null || value === undefined || value === "" ? "-" : String(value);
  }

  return null;

}

export const DataTable = <T,>({
  columns,
  data,
  getRowKey,
  isLoading,
  onRowClick,
}: DataTableProps<T>) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <TablePlaceHolder />;
  }
  return (
    <div className="tableWrapper">
      <table className="dataTable">
        <thead className="tableHead">
          <tr>
            {columns.map((column) => (
              <th className="tableHeader" key={column.header}>
                {t(`common:fields.${column.labelKey}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tableBody">
          {data.map((row, rowIndex) => (
            <tr
              className="tableRow"
              key={getRowKey(row, rowIndex)}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((column) => (
                <td
                  className="tableCell"
                  key={`${getRowKey(row, rowIndex)}-${column.header}`}
                >
                  {getCellValue(row, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
